import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  Card,
  CardContent,
  Badge,
} from '@mui/material';
import {
  Event,
  LocationOn,
  Person,
  Visibility,
  Edit,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  parseISO,
  isSameMonth,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  isWithinInterval,
  startOfDay,
  endOfDay,
} from 'date-fns';
import { getCalendarUserColor } from '../../utils/calendarUserColors';

interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  eventType: 'client_meeting' | 'court_hearing' | 'deposition' | 'mediation' | 'consultation' | 'filing_deadline' | 'document_review' | 'billing_review' | 'staff_meeting' | 'training' | 'other';
  startDate: string;
  endDate: string;
  allDay: boolean;
  location: string | null;
  status: 'scheduled' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  reminderEnabled: boolean;
  reminderTime: number;
  reminderSent: boolean;
  notes: string | null;
  attendees: any[];
  recurrence: any;
  case: { id: string; caseNumber: string; title: string; } | null;
  client: { id: string; name: string; } | null;
  task: { id: string; title: string; status: string; } | null;
  creator: { id: string; name: string; email: string; } | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface MonthViewProps {
  events: CalendarEvent[];
  currentDate: Date;
  onEventClick: (event: CalendarEvent) => void;
  onEventEdit: (event: CalendarEvent) => void;
  onEventView: (event: CalendarEvent) => void;
  onDateChange: (date: Date) => void;
}

const MonthView: React.FC<MonthViewProps> = ({
  events,
  currentDate,
  onEventClick,
  onEventEdit,
  onEventView,
  onDateChange,
}) => {
  const theme = useTheme();
  const [hoveredEvent, setHoveredEvent] = useState<string | null>(null);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const getEventColor = (event: CalendarEvent) =>
    getCalendarUserColor(event.creator, theme.palette.grey[500]);

  const getEventsForDay = (date: Date) => {
    const dayEvents = events.filter(event => {
      const eventStart = parseISO(event.startDate);
      const eventEnd = parseISO(event.endDate);
      const dayStart = startOfDay(date);
      const dayEnd = endOfDay(date);
      
      const isInDay = isWithinInterval(eventStart, { start: dayStart, end: dayEnd }) ||
             isWithinInterval(eventEnd, { start: dayStart, end: dayEnd }) ||
             (eventStart <= dayStart && eventEnd >= dayEnd);
      
      // Debug logging for target event
      if (event.id === 'b49cbecc-9833-4cdd-bf90-1457d02df722') {
        console.log('MonthView - Checking target event for day:', format(date, 'yyyy-MM-dd'));
        console.log('Event start:', eventStart);
        console.log('Event end:', eventEnd);
        console.log('Day start:', dayStart);
        console.log('Day end:', dayEnd);
        console.log('Is in day:', isInDay);
      }
      
      return isInDay;
    });
    
    if (dayEvents.length > 0) {
      console.log(`MonthView - Found ${dayEvents.length} events for ${format(date, 'yyyy-MM-dd')}:`, dayEvents.map(e => e.title));
    }
    
    return dayEvents;
  };

  const handleEventClick = (event: CalendarEvent) => {
    console.log('MonthView - Event clicked:', event.title);
    // Always open the event details dialog instead of navigating away
    onEventClick(event);
  };

  const handlePreviousMonth = () => {
    onDateChange(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    onDateChange(addMonths(currentDate, 1));
  };

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <Box>
      {/* Month Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={handlePreviousMonth}>
          <ChevronLeft />
        </IconButton>
        <Typography variant="h5" fontWeight="bold">
          {format(currentDate, 'MMMM yyyy')}
        </Typography>
        <IconButton onClick={handleNextMonth}>
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Calendar Grid */}
      <Paper sx={{ p: 2 }}>
        {/* Week day headers */}
        <Grid container>
          {weekDays.map((day) => (
            <Grid item xs key={day}>
              <Box sx={{ p: 1, textAlign: 'center' }}>
                <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                  {day}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Calendar days */}
        <Grid container>
          {calendarDays.map((day, index) => {
            const dayEvents = getEventsForDay(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isToday = isSameDay(day, new Date());

            return (
              <Grid item xs key={index}>
                <Paper
                  sx={{
                    p: 1,
                    minHeight: 120,
                    backgroundColor: isToday 
                      ? theme.palette.primary.light 
                      : isCurrentMonth 
                        ? theme.palette.background.paper 
                        : theme.palette.action.hover,
                    border: isToday ? `2px solid ${theme.palette.primary.main}` : '1px solid transparent',
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  {/* Day number */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isToday ? 'bold' : 'normal',
                      color: isCurrentMonth ? 'text.primary' : 'text.disabled',
                      mb: 1,
                    }}
                  >
                    {format(day, 'd')}
                  </Typography>

                  {/* Events */}
                  <Box sx={{ position: 'relative' }}>
                    {dayEvents.slice(0, 3).map((event, eventIndex) => (
                      <Card
                        key={event.id}
                        sx={{
                          mb: 0.5,
                          backgroundColor: getEventColor(event),
                          color: 'white',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                            transform: 'scale(1.02)',
                            zIndex: 10,
                          },
                          transition: 'all 0.2s ease-in-out',
                          zIndex: hoveredEvent === event.id ? 10 : 1,
                        }}
                        onMouseEnter={() => setHoveredEvent(event.id)}
                        onMouseLeave={() => setHoveredEvent(null)}
                        onClick={() => handleEventClick(event)}
                      >
                        <CardContent sx={{ p: 0.5, '&:last-child': { pb: 0.5 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 'bold',
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.6rem',
                                }}
                              >
                                {event.title}
                              </Typography>
                              {event.location && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <LocationOn sx={{ fontSize: '0.5rem' }} />
                                  <Typography variant="caption" sx={{ fontSize: '0.5rem' }}>
                                    {event.location}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            
                            {hoveredEvent === event.id && (
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="View">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onEventView(event);
                                    }}
                                    sx={{ color: 'white', p: 0.5 }}
                                  >
                                    <Visibility sx={{ fontSize: '0.6rem' }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onEventEdit(event);
                                    }}
                                    sx={{ color: 'white', p: 0.5 }}
                                  >
                                    <Edit sx={{ fontSize: '0.6rem' }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>

                          {/* Case indicator */}
                          {event.case && (
                            <Chip
                              label={event.case.caseNumber}
                              size="small"
                              sx={{
                                mt: 0.5,
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                fontSize: '0.5rem',
                                height: 12,
                              }}
                            />
                          )}
                        </CardContent>
                      </Card>
                    ))}

                    {/* More events indicator */}
                    {dayEvents.length > 3 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.6rem',
                          fontStyle: 'italic',
                        }}
                      >
                        +{dayEvents.length - 3} more
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </Box>
  );
};

export default MonthView; 